import React, { createContext, useContext, useEffect, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { config } from "@/lib/config";

// Static libraries array - MUST be outside component to prevent reload
const libraries: "places"[] = ["places"] as const;

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: undefined,
});

export const useGoogleMapsLoader = () => useContext(GoogleMapsContext);

/**
 * Global Google Maps loader - loads API once for entire app
 * Prevents "LoadScript has been reloaded unintentionally" warning
 */
export const GoogleMapsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [billingError, setBillingError] = useState<Error | undefined>(
    undefined
  );

  // Check if API key is missing or invalid
  const apiKey = config.googleMaps.apiKey;
  const hasValidApiKey = 
    apiKey && 
    apiKey.trim() !== "" && 
    apiKey !== "YOUR_GOOGLE_MAPS_API_KEY" &&
    apiKey !== "your_google_maps_api_key_here" &&
    apiKey !== "undefined";

  // Listen for Google Maps billing errors - use capture phase to catch early
  useEffect(() => {
    const handleGoogleMapsError = (event: ErrorEvent | Event) => {
      const errorEvent = event as ErrorEvent;
      const error = errorEvent.error;

      // Check if it's a Google Maps error using structured type checks
      if (error) {
        // Check for Google Maps error classes by constructor name
        const errorType = error.constructor?.name;
        const errorCode = error.code;

        // Handle Google Maps specific error types
        const isMapsError =
          errorType === "MapsRequestError" ||
          errorType === "MapsServerError" ||
          errorType === "MapsNetworkError" ||
          errorType === "InvalidKeyMapError" ||
          errorType === "BillingNotEnabledMapError" ||
          errorType === "RefererNotAllowedMapError";

        // Check error codes for billing/authorization issues
        const isBillingError =
          errorCode === "BILLING_NOT_ENABLED" ||
          errorCode === "INVALID_KEY" ||
          errorCode === "REFERER_NOT_ALLOWED" ||
          errorCode === "REQUEST_DENIED" ||
          errorCode === "OVER_QUERY_LIMIT";

        if (isMapsError || isBillingError) {
          const errorMessage = errorCode
            ? `Google Maps API Error: ${errorCode}`
            : `Google Maps API Error: ${errorType}`;

          console.warn("Google Maps API Error detected:", {
            type: errorType,
            code: errorCode,
            message: error.message,
          });

          setBillingError(new Error(errorMessage));

          // Prevent the error from showing in console
          event.preventDefault();
          event.stopImmediatePropagation();
          return false;
        }
      }
    };

    // Listen in capture phase to catch errors early
    window.addEventListener("error", handleGoogleMapsError, true);

    // Also listen for unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;

      if (error) {
        // Check for Google Maps error classes
        const errorType = error.constructor?.name;
        const errorCode = error.code;

        const isMapsError =
          errorType === "MapsRequestError" ||
          errorType === "MapsServerError" ||
          errorType === "MapsNetworkError" ||
          errorType === "InvalidKeyMapError" ||
          errorType === "BillingNotEnabledMapError" ||
          errorType === "RefererNotAllowedMapError";

        const isBillingError =
          errorCode === "BILLING_NOT_ENABLED" ||
          errorCode === "INVALID_KEY" ||
          errorCode === "REFERER_NOT_ALLOWED" ||
          errorCode === "REQUEST_DENIED" ||
          errorCode === "OVER_QUERY_LIMIT";

        if (isMapsError || isBillingError) {
          const errorMessage = errorCode
            ? `Google Maps API Error: ${errorCode}`
            : `Google Maps API Error: ${errorType}`;

          console.warn("Google Maps Promise Rejection:", {
            type: errorType,
            code: errorCode,
            message: error.message,
          });

          setBillingError(new Error(errorMessage));
          event.preventDefault();
        }
      }
    };

    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleGoogleMapsError, true);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  // Call useJsApiLoader unconditionally to follow Rules of Hooks
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: libraries, // Static array prevents reload
  });

  // If no valid API key, override hook results with error state
  if (!hasValidApiKey) {
    console.warn("Google Maps API key is not configured. Using fallback map view.");
    return (
      <GoogleMapsContext.Provider
        value={{
          isLoaded: false,
          loadError: new Error("Google Maps API key is not configured"),
        }}
      >
        {children}
      </GoogleMapsContext.Provider>
    );
  }

  // Combine load errors and billing errors
  const finalError = loadError || billingError;

  return (
    <GoogleMapsContext.Provider
      value={{ isLoaded: isLoaded && !billingError, loadError: finalError }}
    >
      {children}
    </GoogleMapsContext.Provider>
  );
};
